import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/popup.css';

import useAuthenticate from "../authentication/authenticate.js";
import imageUpload from '../general/imageUpload.js';

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';

import { green } from '@mui/material/colors';
import { inputBaseClasses } from '@mui/material/InputBase';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const EditUnitForm = ({ propertyId, unitId, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        unitNumber: '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        parking: '',
        lotArea: '',
        floorArea: '',
        description: '',
        availableFrom: '',
        isAvailable: false,
        images: [],
    });
    const [error, setError] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const [unitImages, setUnitImages] = useState([]);

    const { user } = useAuthenticate();
    const activeAccountId = Object.values(user?.user?.active || {})[0];

    // Fetch unit data for editing
    useEffect(() => {
        const fetchUnitData = async () => {
            try {
                const response = await fetch(`${config.API_URL}/v2/unit/${unitId}`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
                });
                if (response.ok) {
                    const unit = await response.json();
                    setFormData({
                        ...unit,
                        property: propertyId || unit.property, // Ensure propertyId is set
                    });

                    // Preload existing images into the Upload component
                    if (unit.images && unit.images.length > 0) {
                        setUnitImages(unit.images.map((img) => ({
                            uid: img._id,
                            name: img.altText || "Unit image",
                            status: "done",
                            url: img.key,
                        })));
                    }
                } else {
                    setError('Failed to fetch unit data.');
                }
            } catch (err) {
                setError('Error loading unit data.');
            }
        };

        if (unitId) fetchUnitData();
    }, [unitId, propertyId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: name === 'isAvailable' ? e.target.checked : value,
        }));
    };

    const handleSubmit = async (payloadOverride, e) => {
        if (e && e.preventDefault) e.preventDefault();
        setError('');

        const dataToSend = payloadOverride || formData;
        console.log("Sending to backend:", dataToSend);

        try {
            const response = await fetch(`${config.API_URL}/v2/unit/${unitId}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                toast.error('Failed to update unit.');
            } else {
                const data = await response.json();
                console.log('Updated unit:', data);
                toast.success('Unit updated successfully!');
                onSave(data._id);
                onCancel();
            }
        } catch (err) {
            toast.error('An unexpected error occurred.');
        }
    };

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            <PlusOutlined />
            <div className="font-poppins" style={{ marginTop: 4 }}>Upload</div>
        </button>
    );

    const steps = [
        'Input unit details',
        'Upload unit images',
    ];

    const isStepOptional = (step) => {
        return step === 1;
    };

    const isStepSkipped = (step) => {
        return skipped.has(step);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNext = async () => {
        try {
            const newFiles = unitImages.filter(f => f.originFileObj);
            const existingImages = unitImages.filter(f => !f.originFileObj && f.url);

            let uploadedImages = [];

            if (newFiles.length > 0) {
                const uploadedImageIds = await imageUpload(
                    newFiles.map(f => ({ name: f.name, type: f.originFileObj.type, data: f.originFileObj })),
                    "unit",
                    propertyId,
                    activeAccountId,
                    newFiles.map(() => "Unit image")
                );

                uploadedImages = uploadedImageIds; // array of IDs
            }

            const mergedImages = [
                ...existingImages.map(img => img.uid), // existing ones already in DB
                ...uploadedImages                      // new ones from backend
            ];

            const payload = {
                ...formData,
                images: mergedImages,
            };

            if (activeStep === steps.length - 1) {
                await handleSubmit(payload);
            } else {
                setFormData(payload);
                setActiveStep(prev => prev + 1);
            }
        } catch (error) {
            message.error("Failed to upload images. Please try again.");
        }
    };

    const handleSkip = () => {
        console.log("🟢 Skip pressed inside EditUnitForm. Current step:", activeStep);

        if (!isStepOptional(activeStep)) {
            toast.error("You can’t skip a non-optional step.");
            return;
        }

        if (activeStep === steps.length - 1) {
            handleSubmit();
            return;
        }

        setSkipped(prevSkipped => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(activeStep);
            console.log("🟡 Skipped steps now:", [...newSkipped]);
            return newSkipped;
        });

        setActiveStep(prev => {
            const nextStep = prev + 1;
            console.log("➡️ Moving to step:", nextStep);
            return nextStep;
        });
    };


    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onCancel}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">Edit Unit</h2>
                <div>
                    <Stepper activeStep={activeStep} style={{ marginBottom: '30px' }} alternativeLabel>
                        {steps.map((label, index) => {
                            const stepProps = {};
                            const labelProps = {};
                            if (isStepOptional(index)) {
                                labelProps.optional = (
                                    <Typography variant="caption">Optional</Typography>
                                );
                            }
                            if (isStepSkipped(index)) {
                                stepProps.completed = false;
                            }
                            return (
                                <Step
                                    key={label}
                                    {...stepProps}
                                    sx={{
                                        "& .MuiStepIcon-root.Mui-active": {
                                            color: "#C86B2C",
                                        },
                                        "& .MuiStepIcon-root.Mui-completed": {
                                            color: "#C86B2C",
                                        },
                                    }}>
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                        {activeStep === 0 && (
                            <div>
                                <div className="popup-columns">
                                    <TextField
                                        label="Unit Number"
                                        name="unitNumber"
                                        variant="outlined"
                                        fullWidth
                                        value={formData.unitNumber}
                                        onChange={handleChange}
                                        sx={{ marginBottom: '1rem' }}
                                        required
                                    />
                                    <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                                        <InputLabel htmlFor="outlined-adornment-price">Price</InputLabel>
                                        <OutlinedInput
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            type="number"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            startAdornment={<InputAdornment position="start">PHP</InputAdornment>}
                                            label="Price"
                                        />
                                    </FormControl>
                                </div>
                                <div className="popup-columns">
                                    <TextField
                                        label="Bedrooms"
                                        name="bedrooms"
                                        variant="outlined"
                                        value={formData.bedrooms}
                                        onChange={handleChange}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        sx={{ width: '100%', marginBottom: '1rem' }}
                                    />
                                    <TextField
                                        label="Bathrooms"
                                        name="bathrooms"
                                        variant="outlined"
                                        value={formData.bathrooms}
                                        onChange={handleChange}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        sx={{ width: '100%', marginBottom: '1rem' }}
                                    />
                                </div>
                                <div className="popup-columns">
                                    <TextField
                                        label="Lot Area"
                                        name="lotArea"
                                        variant="outlined"
                                        value={formData.lotArea}
                                        onChange={handleChange}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment
                                                        position="end"
                                                        sx={{
                                                            opacity: 0,
                                                            pointerEvents: 'none',
                                                            [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        m²
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{ width: '100%', marginBottom: '1rem' }}
                                    />
                                    <TextField
                                        label="Floor Area"
                                        name="floorArea"
                                        variant="outlined"
                                        value={formData.floorArea}
                                        onChange={handleChange}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment
                                                        position="end"
                                                        sx={{
                                                            opacity: 0,
                                                            pointerEvents: 'none',
                                                            [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        m²
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{ width: '100%', marginBottom: '1rem' }}
                                    />
                                    <TextField
                                        label="Parking Spaces"
                                        name="parking"
                                        variant="outlined"
                                        value={formData.parking}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        onChange={handleChange}
                                        sx={{ width: '100%', marginBottom: '1rem' }}
                                    />
                                </div>
                                <TextField
                                    label="Description"
                                    name="description"
                                    multiline
                                    fullWidth
                                    rows={3}
                                    value={formData.description}
                                    onChange={handleChange}
                                    sx={{ marginBottom: '1rem' }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Tooltip title={!formData.isAvailable ? "This can only be edited if the unit is available" : ""} arrow>
                                        <span>
                                            <DatePicker
                                                label="Available From"
                                                value={formData.availableFrom ? dayjs(formData.availableFrom) : null}
                                                onChange={(newValue) =>
                                                    setFormData((prevData) => ({
                                                        ...prevData,
                                                        availableFrom: newValue ? newValue.toISOString() : '',
                                                    }))
                                                }
                                                disabled={!formData.isAvailable}
                                                sx={{ width: '100%', marginBottom: '1rem' }}
                                            />
                                        </span>
                                    </Tooltip>
                                </LocalizationProvider>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.isAvailable}
                                            onChange={(e) =>
                                                setFormData((prevData) => ({
                                                    ...prevData,
                                                    isAvailable: e.target.checked, // Update state correctly
                                                }))
                                            }
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: green[600],
                                                },
                                            }}
                                        />
                                    }
                                    label="Available"
                                    sx={{
                                        fontSize: '14px',
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                            </div>
                        )}
                        {activeStep === 1 && (
                            <div>
                                <Upload
                                    multiple
                                    listType="picture-card"
                                    fileList={unitImages}
                                    showUploadList={{ showRemoveIcon: false }}
                                    beforeUpload={(file) => {
                                        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                                        if (!isJpgOrPng) {
                                            message.error('You can only upload JPG/PNG file!');
                                            return Upload.LIST_IGNORE;
                                        }
                                        const isLt2M = file.size / 1024 / 1024 < 2;
                                        if (!isLt2M) {
                                            message.error('Image must be smaller than 2MB!');
                                            return Upload.LIST_IGNORE;
                                        }

                                        // Add new file (not yet uploaded)
                                        setUnitImages(prev => [
                                            ...prev,
                                            {
                                                uid: file.uid,
                                                name: file.name,
                                                status: "done",
                                                originFileObj: file,
                                                url: URL.createObjectURL(file)
                                            }
                                        ]);

                                        return false; // prevents auto-upload
                                    }}
                                >
                                    {uploadButton}
                                </Upload>
                                <div className="font-poppins text-sm text-gray-500 mt-4">
                                    Optional: Upload unit image(s). JPG/PNG, max 2MB
                                </div>
                            </div>
                        )}
                    </div>
                    <React.Fragment>
                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, paddingLeft: '10px', paddingRight: '10px' }}>
                            <button
                                className={`stepper-button ${activeStep === 0 ? "disabled" : ""}`}
                                disabled={activeStep === 0}
                                onClick={handleBack}
                            >
                                Back
                            </button>
                            <Box sx={{ flex: '1 1 auto' }} />
                            {isStepOptional(activeStep) && (
                                <Button color="inherit" onClick={handleSkip} sx={{ mr: 1, textTransform: "none", borderRadius: "10px" }}>
                                    Skip
                                </Button>
                            )}
                            <Tooltip
                                title={activeStep === 1 && unitImages.length === 0 ? "Click Skip if you don’t want to add any images" : ""}
                            >
                                <span>
                                    <button
                                        className="stepper-button"
                                        onClick={handleNext}
                                        disabled={activeStep === 1 && unitImages.length === 0} // block Finish unless they upload OR Skip
                                    >
                                        {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                    </button>
                                </span>
                            </Tooltip>
                        </Box>
                    </React.Fragment>
                </div>
                {error && <p className="error-text">{error}</p>}
            </div>
        </div>
    );
};

export default EditUnitForm;
